import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Users, Building2, CheckCircle,
  XCircle, Clock, AlertTriangle,
  TrendingUp, TrendingDown, MapPin, Download,
  FileText, UserCheck, UserX, ChevronDown, ChevronUp
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, PieChart, Pie, Cell,
  ResponsiveContainer
} from 'recharts';

const StatCard = ({ title, value, icon: Icon, color, subtitle, dark }) => (
  dark ? (
    <div className={`rounded-xl shadow p-6 ${color}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white/70 text-sm">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-xs text-white/50 mt-1">{subtitle}</p>}
        </div>
        <div className="p-3 rounded-lg bg-white/10">
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  ) : (
    <div className={`bg-white rounded-xl shadow p-6 border-l-4 ${color}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className="p-3 rounded-lg bg-gray-50">
          <Icon size={24} className="text-gray-400" />
        </div>
      </div>
    </div>
  )
);

// ── SUPER ADMIN DASHBOARD ──────────────────────────
const SuperAdminDashboard = ({ data }) => {
  const attendancePieData = [
    { name: 'Present', value: parseInt(data?.attendance?.present) || 0, color: '#22c55e' },
    { name: 'Late', value: parseInt(data?.attendance?.late) || 0, color: '#f59e0b' },
    { name: 'Half Day', value: parseInt(data?.attendance?.half_day) || 0, color: '#f97316' },
    { name: 'Absent', value: parseInt(data?.attendance?.absent) || 0, color: '#ef4444' },
  ].filter(item => item.value > 0);

  const districtBarData = data?.districtPerformance
    ?.filter(d => parseInt(d.total_officers) > 0)
    ?.slice(0, 10)
    ?.map(d => ({
      name: d.district_name?.length > 10 ? d.district_name.substring(0, 10) + '...' : d.district_name,
      Officers: parseInt(d.total_officers) || 0,
      Present: parseInt(d.present_count) || 0,
      Visits: parseInt(d.complete_visits) || 0,
    })) || [];

  const divisionData = data?.districtPerformance?.reduce((acc, d) => {
    const div = d.division_name;
    if (!acc[div]) acc[div] = { name: div, officers: 0, present: 0 };
    acc[div].officers += parseInt(d.total_officers) || 0;
    acc[div].present += parseInt(d.present_count) || 0;
    return acc;
  }, {});
  const divisionChartData = Object.values(divisionData || {}).filter(d => d.officers > 0);

  return (
    <>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard title="Present Today" value={data?.attendance?.present || 0} icon={CheckCircle} color="border-l-4 border-green-600" />
        <StatCard title="Late Today" value={data?.attendance?.late || 0} icon={Clock} color="border-l-4 border-yellow-500" />
        <StatCard title="Absent Today" value={data?.attendance?.absent || 0} icon={XCircle} color="border-l-4 border-red-500" />
        <StatCard title="Half Day" value={data?.attendance?.half_day || 0} icon={AlertTriangle} color="border-l-4 border-orange-500" />
      </div>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard title="Complete Visits" value={data?.visits?.complete || 0} icon={Building2} color="border-l-4 border-blue-600" subtitle="This month" />
        <StatCard title="Reports Submitted" value={data?.reports?.submitted || 0} icon={CheckCircle} color="border-l-4 border-purple-600" subtitle="This month" />
        <StatCard title="Total Users" value={data?.users?.total || 0} icon={Users} color="border-l-4 border-indigo-600" subtitle={`${data?.users?.officers || 0} officers`} />
      </div>

      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Today's Attendance Overview</h2>
          {attendancePieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={attendancePieData} cx="50%" cy="50%" outerRadius={90} dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}>
                  {attendancePieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400">No attendance data for today</div>
          )}
        </div>
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Officers by Division</h2>
          {divisionChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={divisionChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Legend />
                <Bar dataKey="officers" name="Officers" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="present" name="Present" fill="#22c55e" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400">No data available</div>
          )}
        </div>
      </div>

      {districtBarData.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">District Performance (Active Districts)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={districtBarData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Legend />
              <Bar dataKey="Officers" fill="#6366f1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Present" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Visits" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">District Performance Table</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Division</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Officers</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Present</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Visits</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {data?.districtPerformance?.map((district, index) => (
                <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-800">{district.district_name}</td>
                  <td className="px-4 py-3 text-gray-500">{district.division_name}</td>
                  <td className="px-4 py-3 text-center">{district.total_officers}</td>
                  <td className="px-4 py-3 text-center">{district.present_count}</td>
                  <td className="px-4 py-3 text-center">{district.complete_visits}</td>
                  <td className="px-4 py-3 text-center">
                    {parseInt(district.total_officers) > 0 ? (
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">Active</span>
                    ) : (
                      <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs font-medium">No Data</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ── EXECUTIVE DASHBOARD (Secretary/Director) ───────
const ExecutiveDashboard = ({ data }) => {
  const [sectorView, setSectorView] = useState('combined');
  const [activeTab, setActiveTab] = useState('overview');
  const [activeDetail, setActiveDetail] = useState(null);
  const toggleDetail = (key) => setActiveDetail(prev => prev === key ? null : key);

  const handleExportPDF = () => {
    import('jspdf').then(({ default: jsPDF }) => {
      import('jspdf-autotable').then(({ default: autoTable }) => {
        const doc = new jsPDF();

        // Header
        doc.setFillColor(30, 58, 138);
        doc.rect(0, 0, 210, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('BBSHRRDB', 14, 12);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Benazir Bhutto Shaheed Human Resource Research & Development Board', 14, 18);
        doc.text('Universities & Boards Department — Government of Sindh', 14, 23);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text('Monthly Executive Summary Report', 14, 30);
        doc.setFontSize(8);
        doc.setTextColor(191, 219, 254);
        doc.text(`Generated: ${new Date().toLocaleString('en-PK')}`, 196, 12, { align: 'right' });
        doc.text(`Month: ${data?.month}/${data?.year}`, 196, 17, { align: 'right' });
        doc.setTextColor(0, 0, 0);

        let y = 45;

        // Province Wide KPIs
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(30, 58, 138);
        doc.setTextColor(255, 255, 255);
        doc.rect(14, y, 182, 7, 'F');
        doc.text('Province-Wide KPIs', 16, y + 5);
        doc.setTextColor(0, 0, 0);
        y += 12;

        autoTable(doc, {
          startY: y,
          head: [['Metric', 'Value', 'Details']],
          body: [
            ['Attendance Rate', `${data?.provinceWide?.attendance?.attendance_percent || 0}%`,
              `Present: ${data?.provinceWide?.attendance?.total_present || 0} | Absent: ${data?.provinceWide?.attendance?.total_absent || 0}`],
            ['Visit Compliance', `${data?.provinceWide?.visits?.visit_percent || 0}%`,
              `Complete: ${data?.provinceWide?.visits?.complete || 0} | Incomplete: ${data?.provinceWide?.visits?.incomplete || 0}`],
            ['Report Submission', `${data?.provinceWide?.reports?.total > 0
              ? Math.round((data?.provinceWide?.reports?.submitted / data?.provinceWide?.reports?.total) * 100) : 0}%`,
              `Submitted: ${data?.provinceWide?.reports?.submitted || 0} | Draft: ${data?.provinceWide?.reports?.draft || 0}`],
            ['Active Officers', data?.provinceWide?.users?.officers || 0,
              `Total Users: ${data?.provinceWide?.users?.total || 0}`],
          ],
          styles: { fontSize: 9 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255 },
          alternateRowStyles: { fillColor: [239, 246, 255] },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;

        // Sector Summary
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(30, 58, 138);
        doc.setTextColor(255, 255, 255);
        doc.rect(14, y, 182, 7, 'F');
        doc.text('Sector Summary', 16, y + 5);
        doc.setTextColor(0, 0, 0);
        y += 12;

        autoTable(doc, {
          startY: y,
          head: [['Sector', 'Institutions', 'Visits', 'Reports']],
          body: [
            ['Public', data?.publicSector?.total_institutions || 0,
              data?.publicSector?.complete_visits || 0,
              data?.publicSector?.submitted_reports || 0],
            ['Private', data?.privateSector?.total_institutions || 0,
              data?.privateSector?.complete_visits || 0,
              data?.privateSector?.submitted_reports || 0],
          ],
          styles: { fontSize: 9 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255 },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 10;

        // Officer Performance
        if (y > 200) { doc.addPage(); y = 20; }
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setFillColor(30, 58, 138);
        doc.setTextColor(255, 255, 255);
        doc.rect(14, y, 182, 7, 'F');
        doc.text('Officer Performance', 16, y + 5);
        doc.setTextColor(0, 0, 0);
        y += 12;

        autoTable(doc, {
          startY: y,
          head: [['#', 'Officer Name', 'Employee ID', 'District', 'Division', 'Attendance %', 'Visits', 'Reports']],
          body: (data?.officerPerformance || []).map((o, i) => [
            i + 1, o.full_name, o.employee_id || '—',
            o.district_name || '—', o.division_name || '—',
            `${o.attendance_percent || 0}%`,
            o.complete_visits || 0,
            o.submitted_reports || 0,
          ]),
          styles: { fontSize: 7 },
          headStyles: { fillColor: [59, 130, 246], textColor: 255 },
          alternateRowStyles: { fillColor: [239, 246, 255] },
          margin: { left: 14, right: 14 },
        });

        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFillColor(243, 244, 246);
          doc.rect(0, 285, 210, 12, 'F');
          doc.setFontSize(8);
          doc.setTextColor(107, 114, 128);
          doc.text('FMAS — Field Monitoring & Attendance System — BBSHRRDB', 14, 291);
          doc.text(`Page ${i} of ${pageCount}`, 196, 291, { align: 'right' });
        }

        doc.save(`Executive_Summary_${data?.month}_${data?.year}.pdf`);
      });
    });
  };

  const divisionChartData = (data?.divisionSummary || []).map(d => ({
    name: d.division_name,
    Attendance: parseFloat(d.attendance_percent) || 0,
    Visits: parseInt(d.complete_visits) || 0,
    Reports: parseInt(d.submitted_reports) || 0,
  }));

  return (
    <>
      {/* Header Row */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          {['combined', 'public', 'private'].map(s => (
            <button key={s} onClick={() => setSectorView(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition
                ${sectorView === s
                  ? 'bg-blue-800 text-white'
                  : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              {s === 'combined' ? '🏛 Combined' : s === 'public' ? '🟢 Public Sector' : '🟣 Private Sector'}
            </button>
          ))}
        </div>
        <button onClick={handleExportPDF}
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm">
          <Download size={16} />
          Export Monthly PDF
        </button>
      </div>

      {/* Province-Wide KPIs */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard title="Attendance Rate" dark
          value={`${data?.provinceWide?.attendance?.attendance_percent || 0}%`}
          icon={CheckCircle} color="bg-emerald-800"
          subtitle={`${data?.provinceWide?.attendance?.total_present || 0} present this month`} />
        <StatCard title="Visit Compliance" dark
          value={`${data?.provinceWide?.visits?.visit_percent || 0}%`}
          icon={Building2} color="bg-blue-900"
          subtitle={`${data?.provinceWide?.visits?.complete || 0} complete this month`} />
        <StatCard title="Report Submission" dark
          value={`${data?.provinceWide?.reports?.total > 0
            ? Math.round((data?.provinceWide?.reports?.submitted / data?.provinceWide?.reports?.total) * 100)
            : 0}%`}
          icon={TrendingUp} color="bg-violet-900"
          subtitle={`${data?.provinceWide?.reports?.submitted || 0} submitted`} />
        <StatCard title="Red Flag Districts" dark
          value={data?.redFlagDistricts?.length || 0}
          icon={AlertTriangle} color="bg-rose-900"
          subtitle="Below 70% attendance" />
      </div>

      {/* Today's Performance & Alerts */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <div className="col-span-2 bg-slate-800 rounded-xl shadow p-6">
          <h2 className="text-lg font-bold text-white mb-1">Today's Performance — Province-Wide</h2>
          <p className="text-xs text-slate-400 mb-4">Aggregated from officer visit reports filed today across Sindh</p>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-5 flex items-center gap-4">
              <Users size={28} className="text-blue-800" />
              <div>
                <p className="text-blue-600 text-sm">Trainees Allocated</p>
                <p className="text-3xl font-bold text-blue-800">{data?.todayPerformance?.trainees_allocated || 0}</p>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-5 flex items-center gap-4">
              <UserCheck size={28} className="text-green-800" />
              <div>
                <p className="text-green-600 text-sm">Trainees Present</p>
                <p className="text-3xl font-bold text-green-800">{data?.todayPerformance?.trainees_present || 0}</p>
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-5 flex items-center gap-4">
              <UserX size={28} className="text-red-800" />
              <div>
                <p className="text-red-600 text-sm">Trainees Absent</p>
                <p className="text-3xl font-bold text-red-800">{data?.todayPerformance?.trainees_absent || 0}</p>
              </div>
            </div>
            <button
              onClick={() => toggleDetail('officers')}
              className={`bg-purple-50 rounded-lg p-5 flex items-center justify-between gap-4 border-2 ${activeDetail === 'officers' ? 'border-purple-400' : 'border-transparent'}`}
            >
              <div className="flex items-center gap-4">
                <UserCheck size={28} className="text-purple-800" />
                <div className="text-left">
                  <p className="text-purple-600 text-sm">Officers in Field</p>
                  <p className="text-3xl font-bold text-purple-800">{data?.todayPerformance?.officers_in_field || 0}</p>
                </div>
              </div>
              {activeDetail === 'officers' ? <ChevronUp size={20} className="text-purple-600" /> : <ChevronDown size={20} className="text-purple-600" />}
            </button>
            <button
              onClick={() => toggleDetail('visits')}
              className={`bg-teal-50 rounded-lg p-5 flex items-center justify-between gap-4 border-2 ${activeDetail === 'visits' ? 'border-teal-400' : 'border-transparent'}`}
            >
              <div className="flex items-center gap-4">
                <Building2 size={28} className="text-teal-800" />
                <div className="text-left">
                  <p className="text-teal-600 text-sm">Visits Conducted</p>
                  <p className="text-3xl font-bold text-teal-800">{data?.todayPerformance?.visits_conducted || 0}</p>
                </div>
              </div>
              {activeDetail === 'visits' ? <ChevronUp size={20} className="text-teal-600" /> : <ChevronDown size={20} className="text-teal-600" />}
            </button>
            <button
              onClick={() => toggleDetail('reports')}
              className={`bg-amber-50 rounded-lg p-5 flex items-center justify-between gap-4 border-2 ${activeDetail === 'reports' ? 'border-amber-400' : 'border-transparent'}`}
            >
              <div className="flex items-center gap-4">
                <FileText size={28} className="text-amber-800" />
                <div className="text-left">
                  <p className="text-amber-600 text-sm">Reports Submitted</p>
                  <p className="text-3xl font-bold text-amber-800">{data?.todayPerformance?.reports_submitted || 0}</p>
                </div>
              </div>
              {activeDetail === 'reports' ? <ChevronUp size={20} className="text-amber-600" /> : <ChevronDown size={20} className="text-amber-600" />}
            </button>
          </div>

          <button
            onClick={() => toggleDetail('live')}
            className={`mt-3 w-full bg-purple-900 rounded-lg p-4 flex items-center justify-between gap-4 border-2 ${activeDetail === 'live' ? 'border-purple-400' : 'border-transparent'}`}
          >
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-60"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-purple-400"></span>
              </span>
              <p className="text-sm font-bold uppercase tracking-wide text-purple-100">Who is Visiting Now</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-white">{data?.whoIsVisitingNow?.length || 0}</p>
                <p className="text-xs text-purple-200 max-w-[160px] text-right leading-tight">Assistant Director Monitoring are live</p>
              </div>
              {activeDetail === 'live' ? <ChevronUp size={20} className="text-purple-200" /> : <ChevronDown size={20} className="text-purple-200" />}
            </div>
          </button>

          {activeDetail === 'live' && (
            <div className="mt-4 border-t border-slate-700 pt-4">
              <p className="text-sm font-semibold text-slate-200 mb-2">
                Officers Currently on a Live Visit ({data?.whoIsVisitingNow?.length || 0})
              </p>
              {data?.whoIsVisitingNow?.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {data.whoIsVisitingNow.map((v, i) => {
                    const hrs = Math.floor(v.duration_minutes / 60);
                    const mins = v.duration_minutes % 60;
                    const durationLabel = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
                    const startTimeLabel = v.visit_start_time
                      ? new Date(v.visit_start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : null;
                    const reportStyles = {
                      'Submitted': 'bg-green-800 text-green-100',
                      'In Progress': 'bg-yellow-800 text-yellow-100',
                      'Not Submitted': 'bg-red-900 text-red-200',
                    };
                    return (
                      <div key={i} className="bg-slate-700 rounded-lg px-3 py-2">
                        <div className="flex justify-between items-start gap-3">
                          <div>
                            <p className="text-sm font-medium text-white">{v.officer_name}</p>
                            <p className="text-xs text-slate-400">{v.institution_name}, {v.district_name}</p>
                          </div>
                          {v.latitude && v.longitude && (
                            <p className="text-xs text-purple-300 flex items-center gap-1 whitespace-nowrap">
                              <MapPin size={12} /> {parseFloat(v.latitude).toFixed(4)}° N, {parseFloat(v.longitude).toFixed(4)}° E
                            </p>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-slate-600">
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-600 text-slate-200 flex items-center justify-center gap-1 whitespace-nowrap">
                            <Clock size={12} /> {startTimeLabel ? `Visit Started: ${startTimeLabel}` : 'Visit Started: —'}
                          </span>
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-800 text-purple-100 flex items-center justify-center gap-1 whitespace-nowrap">
                            <Clock size={12} /> Duration: {durationLabel}
                          </span>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full flex items-center justify-center gap-1 whitespace-nowrap ${reportStyles[v.report_status]}`}>
                            <FileText size={12} /> PMR: {v.report_status}
                            {v.report_status === 'Submitted' && v.report_id && (
                              <a
                                href={`/reports/${v.report_id}/print`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={e => e.stopPropagation()}
                                className="ml-1 bg-white text-green-800 rounded px-1 py-0.5 font-bold hover:bg-green-100 transition-colors"
                                title="View PDF Report"
                              >
                                PDF
                              </a>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No officers currently on a live visit</p>
              )}
            </div>
          )}

          {activeDetail === 'officers' && (
            <div className="mt-4 border-t border-slate-700 pt-4">
              <p className="text-sm font-semibold text-slate-200 mb-2">
                Officers in Field Today ({data?.officersInField?.length || 0})
              </p>
              {data?.officersInField?.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {data.officersInField.map((o, i) => (
                    <div key={i} className="flex justify-between items-center bg-slate-700 rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-white">{o.full_name}</p>
                        <p className="text-xs text-slate-400">{o.role}{o.district_name ? ` — ${o.district_name}` : ''}</p>
                      </div>
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        {o.visits_count} visit{o.visits_count === 1 ? '' : 's'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No officers in the field today</p>
              )}
            </div>
          )}

          {activeDetail === 'visits' && (
            <div className="mt-4 border-t border-slate-700 pt-4">
              <p className="text-sm font-semibold text-slate-200 mb-2">
                Institutes Under Visit Today ({data?.institutesUnderVisit?.length || 0})
              </p>
              {data?.institutesUnderVisit?.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {data.institutesUnderVisit.map((inst, i) => (
                    <div key={i} className="flex justify-between items-center bg-slate-700 rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-white">{inst.institution_name}</p>
                        <p className="text-xs text-slate-400">{inst.district_name}, {inst.division_name} division</p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full
                        ${inst.status === 'Complete' ? 'bg-slate-600 text-slate-300' : 'bg-green-100 text-green-700'}`}>
                        {inst.status === 'Complete' ? 'Completed' : 'In Progress'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No visits recorded today</p>
              )}
            </div>
          )}

          {activeDetail === 'reports' && (
            <div className="mt-4 border-t border-slate-700 pt-4">
              <p className="text-sm font-semibold text-slate-200 mb-2">
                Reports Submitted Today ({data?.reportsSubmittedList?.length || 0})
              </p>
              {data?.reportsSubmittedList?.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {data.reportsSubmittedList.map((r, i) => (
                    <div key={i} className="flex justify-between items-center bg-slate-700 rounded-lg px-3 py-2">
                      <div>
                        <p className="text-sm font-medium text-white">{r.institution_name}</p>
                        <p className="text-xs text-slate-400">{r.district_name}{r.pmr_no ? ` · PMR #${r.pmr_no}` : ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400">No reports submitted today</p>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Alerts &amp; Compliance</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-lg p-3">
              <AlertTriangle size={16} className="text-red-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-800">{data?.alerts?.overdue_reports || 0} reports overdue</p>
                <p className="text-xs text-gray-500">Draft &gt; 7 days since visit</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-100 rounded-lg p-3">
              <Clock size={16} className="text-yellow-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-800">{data?.alerts?.low_attendance_officers || 0} officers below 60% attendance</p>
                <p className="text-xs text-gray-500">This month</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-100 rounded-lg p-3">
              <Building2 size={16} className="text-yellow-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-800">{data?.alerts?.unvisited_institutions || 0} institutions not visited 30+ days</p>
                <p className="text-xs text-gray-500">Across {data?.alerts?.unvisited_districts || 0} districts</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-lg p-3">
              <TrendingDown size={16} className="text-red-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-800">{data?.alerts?.low_progress_trades || 0} trades below 40% progress</p>
                <p className="text-xs text-gray-500">vs. weekly activity plan</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PMR Report Insights */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">PMR Report Insights</h2>
        <div className="grid grid-cols-4 gap-4 mb-4">
          {(() => {
            const tone = (value, redMax, amberMax) => {
              if (value < redMax) return { bg: 'bg-red-50', label: 'text-red-600', value: 'text-red-800' };
              if (value < amberMax) return { bg: 'bg-amber-50', label: 'text-amber-600', value: 'text-amber-800' };
              return { bg: 'bg-green-50', label: 'text-green-600', value: 'text-green-800' };
            };
            const facilities = data?.pmrInsights?.avg_facilities_score || 0;
            const attendance = data?.pmrInsights?.avg_trainee_attendance || 0;
            const progress = data?.pmrInsights?.avg_academic_progress || 0;
            const passRate = data?.pmrInsights?.avg_exam_pass_rate || 0;
            const tFacilities = tone(facilities, 12, 18);
            const tAttendance = tone(attendance, 60, 80);
            const tProgress = tone(progress, 40, 70);
            const tPassRate = tone(passRate, 50, 75);
            return (
              <>
                <div className={`${tFacilities.bg} rounded-lg p-4`}>
                  <p className={`${tFacilities.label} text-xs`}>Avg Facilities Score</p>
                  <p className={`text-2xl font-bold ${tFacilities.value} mt-1`}>
                    {facilities} <span className="text-sm font-normal opacity-60">/25</span>
                  </p>
                </div>
                <div className={`${tAttendance.bg} rounded-lg p-4`}>
                  <p className={`${tAttendance.label} text-xs`}>Avg Trainee Attendance</p>
                  <p className={`text-2xl font-bold ${tAttendance.value} mt-1`}>{attendance}%</p>
                </div>
                <div className={`${tProgress.bg} rounded-lg p-4`}>
                  <p className={`${tProgress.label} text-xs`}>Avg Academic Progress</p>
                  <p className={`text-2xl font-bold ${tProgress.value} mt-1`}>{progress}%</p>
                </div>
                <div className={`${tPassRate.bg} rounded-lg p-4`}>
                  <p className={`${tPassRate.label} text-xs`}>Avg Exam Pass Rate</p>
                  <p className={`text-2xl font-bold ${tPassRate.value} mt-1`}>{passRate}%</p>
                </div>
              </>
            );
          })()}
        </div>
        {data?.pmrInsights?.lowestFacilities?.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Trades with Lowest Facilities Score</p>
            <div className="space-y-1">
              {data.pmrInsights.lowestFacilities.map((t, i) => (
                <div key={i} className="flex justify-between items-center text-sm py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-gray-700">{t.course_name} — {t.institution_name}, {t.district_name}</span>
                  <span className={`font-semibold ${t.grand_total < 12 ? 'text-red-600' : t.grand_total < 18 ? 'text-amber-600' : 'text-green-600'}`}>
                    {t.grand_total}/25
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sector Cards */}
      {sectorView === 'combined' && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full font-medium">PUBLIC SECTOR</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-500 text-xs">Institutions</p><p className="font-bold text-2xl text-green-700">{data?.publicSector?.total_institutions || 0}</p></div>
              <div><p className="text-gray-500 text-xs">Active Contracts</p><p className="font-bold text-2xl text-green-700">{data?.publicSector?.active_contracts || 0}</p></div>
              <div><p className="text-gray-500 text-xs">Visits This Month</p><p className="font-bold text-xl text-green-600">{data?.publicSector?.complete_visits || 0}</p></div>
              <div><p className="text-gray-500 text-xs">Reports Submitted</p><p className="font-bold text-xl text-green-600">{data?.publicSector?.submitted_reports || 0}</p></div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">PRIVATE SECTOR</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-gray-500 text-xs">Institutions</p><p className="font-bold text-2xl text-purple-700">{data?.privateSector?.total_institutions || 0}</p></div>
              <div><p className="text-gray-500 text-xs">Active Contracts</p><p className="font-bold text-2xl text-purple-700">{data?.privateSector?.active_contracts || 0}</p></div>
              <div><p className="text-gray-500 text-xs">Visits This Month</p><p className="font-bold text-xl text-purple-600">{data?.privateSector?.complete_visits || 0}</p></div>
              <div><p className="text-gray-500 text-xs">Reports Submitted</p><p className="font-bold text-xl text-purple-600">{data?.privateSector?.submitted_reports || 0}</p></div>
            </div>
          </div>
        </div>
      )}

      {sectorView === 'public' && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard title="Total Institutions" value={data?.publicSector?.total_institutions || 0} icon={Building2} color="border-l-4 border-green-600" subtitle="Public sector" />
          <StatCard title="Active Contracts" value={data?.publicSector?.active_contracts || 0} icon={CheckCircle} color="border-l-4 border-green-500" />
          <StatCard title="Visits This Month" value={data?.publicSector?.complete_visits || 0} icon={MapPin} color="border-l-4 border-blue-500" />
          <StatCard title="Reports Submitted" value={data?.publicSector?.submitted_reports || 0} icon={TrendingUp} color="border-l-4 border-purple-500" />
        </div>
      )}

      {sectorView === 'private' && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard title="Total Institutions" value={data?.privateSector?.total_institutions || 0} icon={Building2} color="border-l-4 border-purple-600" subtitle="Private sector" />
          <StatCard title="Active Contracts" value={data?.privateSector?.active_contracts || 0} icon={CheckCircle} color="border-l-4 border-purple-500" />
          <StatCard title="Visits This Month" value={data?.privateSector?.complete_visits || 0} icon={MapPin} color="border-l-4 border-blue-500" />
          <StatCard title="Reports Submitted" value={data?.privateSector?.submitted_reports || 0} icon={TrendingUp} color="border-l-4 border-indigo-500" />
        </div>
      )}

      {/* Red Flag Districts */}
      {data?.redFlagDistricts?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
            <AlertTriangle size={18} /> Red Flag Districts — Below 70% Attendance
          </h3>
          <div className="grid grid-cols-4 gap-3">
            {data.redFlagDistricts.map((d, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-red-100">
                <p className="font-medium text-gray-800 text-sm">{d.district_name}</p>
                <p className="text-gray-500 text-xs">{d.division_name}</p>
                <p className="text-red-600 font-bold text-xl">{d.attendance_percent}%</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {['overview', 'officers', 'districts'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition
              ${activeTab === tab
                ? 'bg-blue-800 text-white'
                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            {tab === 'overview' ? '📊 Division Overview' : tab === 'officers' ? '👥 Officer Performance' : '🗺 District Details'}
          </button>
        ))}
      </div>

      {/* Division Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {divisionChartData.length > 0 && (
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Division-wise Performance</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={divisionChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip /><Legend />
                  <Bar dataKey="Attendance" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Visits" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Reports" fill="#a855f7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-2 gap-6">
            {/* Top 5 */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp size={18} className="text-green-600" />
                Top 5 Performing Districts
              </h3>
              <div className="space-y-3">
                {data?.topDistricts?.map((d, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="bg-green-600 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{d.district_name}</p>
                      <p className="text-xs text-gray-500">{d.division_name}</p>
                    </div>
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                      {d.attendance_percent}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom 5 */}
            <div className="bg-white rounded-xl shadow p-6">
              <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingDown size={18} className="text-red-500" />
                Bottom 5 Performing Districts
              </h3>
              <div className="space-y-3">
                {data?.bottomDistricts?.map((d, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center font-bold">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{d.district_name}</p>
                      <p className="text-xs text-gray-500">{d.division_name}</p>
                    </div>
                    <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-medium">
                      {d.attendance_percent}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Officer Performance Tab */}
      {activeTab === 'officers' && (
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-800">
              Officer Performance — {new Date(0, (data?.month || 1) - 1).toLocaleString('en-PK', { month: 'long' })} {data?.year}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Officer Name</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Employee ID</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Division</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Attendance %</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Visits</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Reports</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Rating</th>
                </tr>
              </thead>
              <tbody>
                {!data?.officerPerformance?.length ? (
                  <tr><td colSpan="9" className="text-center py-8 text-gray-400">No officer data available</td></tr>
                ) : (
                  data.officerPerformance.map((officer, index) => {
                    const rate = parseFloat(officer.attendance_percent) || 0;
                    return (
                      <tr key={officer.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">{officer.full_name}</td>
                        <td className="px-4 py-3 text-gray-500">{officer.employee_id || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{officer.district_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{officer.division_name || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-12 bg-gray-200 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${rate >= 80 ? 'bg-green-500' : rate >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${rate}%` }} />
                            </div>
                            <span className="text-xs font-medium">{rate}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">{officer.complete_visits || 0}</td>
                        <td className="px-4 py-3 text-center">{officer.submitted_reports || 0}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium
                            ${rate >= 80 ? 'bg-green-100 text-green-700' :
                              rate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'}`}>
                            {rate >= 80 ? '⭐ Excellent' : rate >= 60 ? '👍 Good' : '⚠️ Poor'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* District Details Tab */}
      {activeTab === 'districts' && (
        <div className="bg-white rounded-xl shadow">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-bold text-gray-800">All Districts Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-semibold">Division</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Attendance %</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Visits</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Reports</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-semibold">Grade</th>
                </tr>
              </thead>
              <tbody>
                {data?.districtBreakdown?.map((d, i) => {
                  const rate = parseFloat(d.attendance_percent) || 0;
                  return (
                    <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{d.district_name}</td>
                      <td className="px-4 py-3 text-gray-500">{d.division_name}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium
                          ${rate >= 80 ? 'bg-green-100 text-green-700' :
                            rate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'}`}>
                          {d.attendance_percent}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{d.complete_visits}</td>
                      <td className="px-4 py-3 text-center">{d.submitted_reports}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium
                          ${rate >= 80 ? 'bg-green-100 text-green-700' :
                            rate >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'}`}>
                          {rate >= 80 ? 'A' : rate >= 60 ? 'B' : 'C'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

// ── DEPUTY DIRECTOR DASHBOARD ──────────────────────
const DeputyDirectorDashboard = ({ data }) => (
  <>
    <div className="mb-6">
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
        ${data?.sector === 'Public' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
        <Building2 size={16} />
        {data?.sector} Sector Dashboard
      </div>
    </div>
    <div className="bg-white rounded-xl shadow p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-4">
        District Ranking — {data?.sector} Sector
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Rank</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Division</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Attendance %</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Visits</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Reports</th>
            </tr>
          </thead>
          <tbody>
            {data?.districtRanking?.map((d, i) => (
              <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-500 font-medium">#{i + 1}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{d.district_name}</td>
                <td className="px-4 py-3 text-gray-500">{d.division_name}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium
                    ${parseFloat(d.attendance_percent) >= 80 ? 'bg-green-100 text-green-700' :
                      parseFloat(d.attendance_percent) >= 60 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'}`}>
                    {d.attendance_percent}%
                  </span>
                </td>
                <td className="px-4 py-3 text-center">{d.complete_visits}</td>
                <td className="px-4 py-3 text-center">{d.submitted_reports}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </>
);

// ── ASSISTANT DIRECTOR DASHBOARD ───────────────────
const AssistantDirectorDashboard = ({ data }) => (
  <>
    <div className="grid grid-cols-2 gap-4 mb-6">
      <StatCard title="Assigned Districts" value={data?.assignedDistricts || 0}
        icon={MapPin} color="border-l-4 border-blue-600" />
      <StatCard title="Live Visits Today" value={data?.liveVisitFeed?.length || 0}
        icon={Building2} color="border-l-4 border-green-600" subtitle="Field visits in progress" />
    </div>

    <div className="bg-white rounded-xl shadow p-6 mb-6">
      <h2 className="text-lg font-bold text-gray-800 mb-4">District Summary</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
              <th className="text-left px-4 py-3 text-gray-600 font-semibold">Division</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Attendance %</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Visits</th>
              <th className="text-center px-4 py-3 text-gray-600 font-semibold">Reports</th>
            </tr>
          </thead>
          <tbody>
            {data?.districtSummary?.map((d, i) => (
              <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800">{d.district_name}</td>
                <td className="px-4 py-3 text-gray-500">{d.division_name}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium
                    ${parseFloat(d.attendance_percent) >= 80 ? 'bg-green-100 text-green-700' :
                      parseFloat(d.attendance_percent) >= 60 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'}`}>
                    {d.attendance_percent}%
                  </span>
                </td>
                <td className="px-4 py-3 text-center">{d.complete_visits}</td>
                <td className="px-4 py-3 text-center">{d.submitted_reports}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {data?.liveVisitFeed?.length > 0 && (
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Live Visit Feed — Today</h2>
        <div className="space-y-3">
          {data.liveVisitFeed.map((event, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className={`w-3 h-3 rounded-full ${event.event_type === 'Entry' ? 'bg-green-500' : 'bg-red-500'}`} />
              <div className="flex-1">
                <p className="font-medium text-sm text-gray-800">{event.officer_name}</p>
                <p className="text-xs text-gray-500">{event.institution_name} — {event.district_name}</p>
              </div>
              <div className="text-right">
                <p className={`text-xs font-medium ${event.event_type === 'Entry' ? 'text-green-600' : 'text-red-500'}`}>
                  {event.event_type}
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(event.event_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </>
);

// ── OFFICER DASHBOARD ──────────────────────────────
const OfficerDashboard = ({ data }) => {
  const getStatusColor = (status) => {
    const colors = {
      'Present': 'bg-green-500',
      'Late': 'bg-yellow-500',
      'Half Day': 'bg-orange-500',
      'Absent': 'bg-red-500',
    };
    return colors[status] || 'bg-gray-300';
  };

  return (
    <>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard title="Present" value={data?.monthlyStats?.present || 0}
          icon={CheckCircle} color="border-l-4 border-green-600" subtitle="This month" />
        <StatCard title="Late" value={data?.monthlyStats?.late || 0}
          icon={Clock} color="border-l-4 border-yellow-500" subtitle="This month" />
        <StatCard title="Absent" value={data?.monthlyStats?.absent || 0}
          icon={XCircle} color="border-l-4 border-red-500" subtitle="This month" />
        <div className="bg-white rounded-xl shadow p-6 border-l-4 border-blue-600">
          <p className="text-gray-500 text-sm">Performance Score</p>
          <p className="text-3xl font-bold text-blue-800 mt-1">{data?.performanceScore || 0}</p>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${data?.performanceScore || 0}%` }} />
          </div>
        </div>
      </div>

      <div className={`rounded-xl p-4 mb-6 ${
        data?.todayAttendance?.status === 'Present' ? 'bg-green-50 border border-green-200' :
        data?.todayAttendance?.status === 'Late' ? 'bg-yellow-50 border border-yellow-200' :
        'bg-gray-50 border border-gray-200'}`}>
        <p className="font-medium text-gray-700">Today's Status</p>
        {data?.todayAttendance ? (
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span className={`px-3 py-1 rounded-full text-white text-xs font-medium ${getStatusColor(data.todayAttendance.status)}`}>
              {data.todayAttendance.status}
            </span>
            {data.todayAttendance.first_entry_time && (
              <span className="text-gray-600">
                Entry: {new Date(data.todayAttendance.first_entry_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </span>
            )}
            {data.todayAttendance.last_exit_time && (
              <span className="text-gray-600">
                Exit: {new Date(data.todayAttendance.last_exit_time).toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit', hour12: true })}
              </span>
            )}
          </div>
        ) : (
          <p className="text-gray-500 text-sm mt-1">No attendance recorded today</p>
        )}
      </div>

      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Institution Visit Progress — This Month</h2>
        {data?.visitProgress?.length > 0 ? (
          <div className="space-y-3">
            {data.visitProgress.map((inst, i) => {
              const percent = Math.min((parseInt(inst.visits_this_month) / parseInt(inst.required_visits)) * 100, 100);
              return (
                <div key={i} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <p className="text-sm font-medium text-gray-800">{inst.institution_name}</p>
                      <p className="text-xs text-gray-500">{inst.visits_this_month}/{inst.required_visits} visits</p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className={`h-2 rounded-full ${percent >= 100 ? 'bg-green-500' : percent >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full
                    ${percent >= 100 ? 'bg-green-100 text-green-700' :
                      percent >= 50 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'}`}>
                    {Math.round(percent)}%
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">No institutions assigned</p>
        )}
      </div>

      {data?.calendar?.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Monthly Attendance Calendar</h2>
          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-center text-xs font-medium text-gray-500 pb-2">{d}</div>
            ))}
            {data.calendar.map((day, i) => (
              <div key={i} className={`text-center text-xs p-2 rounded-lg ${getStatusColor(day.status)} text-white font-medium`}>
                {new Date(day.date).getDate()}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ── FIELD STAFF DASHBOARD ──────────────────────────
const FieldStaffDashboard = ({ data }) => {
  const getStatusColor = (status) => {
    const colors = {
      'Present': 'bg-green-500',
      'Late': 'bg-yellow-500',
      'Half Day': 'bg-orange-500',
      'Absent': 'bg-red-500',
    };
    return colors[status] || 'bg-gray-300';
  };

  return (
    <>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard title="Present" value={data?.monthlyStats?.present || 0}
          icon={CheckCircle} color="border-l-4 border-green-600" subtitle="This month" />
        <StatCard title="Late" value={data?.monthlyStats?.late || 0}
          icon={Clock} color="border-l-4 border-yellow-500" subtitle="This month" />
        <StatCard title="Half Day" value={data?.monthlyStats?.half_day || 0}
          icon={AlertTriangle} color="border-l-4 border-orange-500" subtitle="This month" />
        <StatCard title="Absent" value={data?.monthlyStats?.absent || 0}
          icon={XCircle} color="border-l-4 border-red-500" subtitle="This month" />
      </div>

      <div className={`rounded-xl p-4 mb-6 ${
        data?.todayAttendance?.status === 'Present' ? 'bg-green-50 border border-green-200' :
        'bg-gray-50 border border-gray-200'}`}>
        <p className="font-medium text-gray-700">Today's Status</p>
        {data?.todayAttendance ? (
          <div className="flex items-center gap-4 mt-2 text-sm">
            <span className={`px-3 py-1 rounded-full text-white text-xs font-medium ${getStatusColor(data.todayAttendance.status)}`}>
              {data.todayAttendance.status}
            </span>
          </div>
        ) : (
          <p className="text-gray-500 text-sm mt-1">No attendance recorded today</p>
        )}
      </div>

      {data?.calendar?.length > 0 && (
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Monthly Attendance Calendar</h2>
          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="text-center text-xs font-medium text-gray-500 pb-2">{d}</div>
            ))}
            {data.calendar.map((day, i) => (
              <div key={i} className={`text-center text-xs p-2 rounded-lg ${getStatusColor(day.status)} text-white font-medium`}>
                {new Date(day.date).getDate()}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

// ── MAIN DASHBOARD ─────────────────────────────────
const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getDashboardEndpoint = (role) => {
    const endpoints = {
      'Super Admin': '/dashboard/super-admin',
      'Secretary': '/dashboard/executive',
      'Director': '/dashboard/executive',
      'Deputy Director Public': '/dashboard/deputy-director',
      'Deputy Director Private': '/dashboard/deputy-director',
      'Assistant Director': '/dashboard/assistant-director',
      'Monitoring Officer': '/dashboard/monitoring-officer',
      'Driver': '/dashboard/field-staff',
      'Peon': '/dashboard/field-staff',
    };
    return endpoints[role] || '/dashboard/super-admin';
  };

  const getDashboardTitle = (role) => {
    const titles = {
      'Super Admin': 'Super Admin Dashboard',
      'Secretary': 'Executive Dashboard',
      'Director': 'Executive Dashboard',
      'Deputy Director Public': 'Deputy Director — Public Sector',
      'Deputy Director Private': 'Deputy Director — Private Sector',
      'Assistant Director': 'Assistant Director Dashboard',
      'Monitoring Officer': 'My Dashboard',
      'Driver': 'My Attendance',
      'Peon': 'My Attendance',
    };
    return titles[role] || 'Dashboard';
  };

  useEffect(() => {
    let isFetching = false;

    const fetchDashboard = async () => {
      if (isFetching) return;
      isFetching = true;
      try {
        const endpoint = getDashboardEndpoint(user?.role);
        const response = await api.get(endpoint);
        setData(response.data);
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
        isFetching = false;
      }
    };

    if (user) fetchDashboard();

    const interval = setInterval(() => {
      if (user) fetchDashboard();
    }, 60000);
    return () => clearInterval(interval);
  }, [user]);

  if (loading) return (
    <Layout title="Dashboard">
      <div className="flex items-center justify-center h-64">
        <div className="text-blue-800 text-lg font-semibold">Loading dashboard...</div>
      </div>
    </Layout>
  );

  const renderDashboard = () => {
    switch (user?.role) {
      case 'Super Admin':
        return <SuperAdminDashboard data={data} />;
      case 'Secretary':
      case 'Director':
        return <ExecutiveDashboard data={data} />;
      case 'Deputy Director Public':
      case 'Deputy Director Private':
        return <DeputyDirectorDashboard data={data} />;
      case 'Assistant Director':
        return <AssistantDirectorDashboard data={data} />;
      case 'Monitoring Officer':
        return <OfficerDashboard data={data} />;
      case 'Driver':
      case 'Peon':
        return <FieldStaffDashboard data={data} />;
      default:
        return <SuperAdminDashboard data={data} />;
    }
  };

  return (
    <Layout title={getDashboardTitle(user?.role)}>
      {renderDashboard()}
    </Layout>
  );
};

export default Dashboard;